import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { CoreBox } from '@youscience/core';
import { ClassRankReporting, GpaWeighting } from '@youscience/commonapp-service-common';

import useRecommenderStore from '@store/RecommenderStore';
import useRecommenderProfileStore, { SectionName } from '@store/RecommenderProfileStore';

import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';
import MonthYearDatePicker from '@components/MonthYearDatePicker';
import SectionAccordion from '@components/SectionAccordion';
import ContinueButton from '@components/ContinueButton';

import { StyledCard } from '@pages/RecommenderSpace/components/Card.styles';

import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';

import { maxDate, minDate } from '../../../constants';
import { SchoolDetailsProps, ClassRankFields } from '../../types';
import useFormSection from '@pages/RecommenderSpace/hooks/useFormSection';
import { FormState } from '@constants/commonApp/types';
import { dateRangeValidation } from '@pages/StudentDetailsPage/components/common/formUtils';

export const ClassRank = ({ onExpand, onSaveAndContinue, isExtended }: SchoolDetailsProps<ClassRankFields>) => {
  const { recommenderProfile } = useRecommenderStore((store) => store);

  const {
    initialState: { classRank: initialState },
    formState: { classRank: formState },
  } = useRecommenderProfileStore((store) => store);

  const initializeValues = () => {
    const { reporting, weighting, startDate, endDate } = recommenderProfile?.schoolProfile?.classRank ?? {};

    return {
      reporting,
      weighting,
      startDate,
      endDate,
    };
  };

  const additionalValidations = (newFormState: FormState<ClassRankFields>) => {
    return dateRangeValidation(newFormState, {});
  };

  const { updateFormValues, handleDateChange, handleRadioChange, handleDateError } = useFormSection<ClassRankFields>({
    initialState,
    formState: formState,
    initializeValues,
    sectionName: SectionName.ClassRank,
    additionalValidations,
  });

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';
  const areAllRequiredFieldsFilled =
    formState.data.reporting === ClassRankReporting.None ||
    (formState.data.startDate && formState.data.endDate && formState.data.weighting);

  const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;

    if (value === ClassRankReporting.None) {
      updateFormValues({ [event.target.name]: value, weighting: undefined, startDate: undefined, endDate: undefined });
    } else {
      updateFormValues({ [event.target.name]: value });
    }
  };

  return (
    <SectionAccordion
      title="Class Rank"
      onChange={onExpand}
      isExpanded={isExtended}
      isChanged={formState.hasChanged}
      isError={isShowErrors && !areAllRequiredFieldsFilled}
    >
      {Object.keys(formState.data).length === 0 ? null : (
        <StyledCard>
          <FormControl
            size="small"
            sx={{ width: '200px', marginTop: '0.5rem' }}
            error={isShowErrors && !formState.data.reporting}
          >
            <InputLabel required>How do you report class rank?</InputLabel>

            <Select
              data-testid="reporting"
              name="reporting"
              label="How do you report class rank? *"
              value={formState.data.reporting ?? ''}
              onChange={handleSelectChange}
            >
              {Object.entries(ClassRankReporting).map(([key, value]) => {
                return (
                  <MenuItem key={value} value={value}>
                    {key}
                  </MenuItem>
                );
              })}
            </Select>

            {isShowErrors && !formState.data.reporting && <FormHelperText>{REQUIRED_FIELD_MESSAGE}</FormHelperText>}
          </FormControl>

          {formState.data.reporting && formState.data.reporting !== ClassRankReporting.None ? (
            <>
              <CoreBox sx={{ display: 'flex', gap: '1.5rem', marginTop: '.5rem', alignItems: 'start' }}>
                <RadioButtonGroupWithClearButton
                  name="weighting"
                  title="Rank Weighting"
                  value={formState.data.weighting ?? undefined}
                  options={GpaWeighting}
                  required
                  handleRadioChange={handleRadioChange}
                  handleClear={() => updateFormValues({ weighting: undefined })}
                  error={isShowErrors && !formState.data.weighting}
                  helperText={isShowErrors && !formState.data.weighting ? REQUIRED_FIELD_MESSAGE : ''}
                />
              </CoreBox>

              <CoreBox sx={{ display: 'flex', gap: '1.5rem', marginTop: '.5rem', alignItems: 'start' }}>
                <MonthYearDatePicker
                  name="startDate"
                  label="From (start date)"
                  required
                  slotProps={{
                    inputProps: { clearable: true, onClear: () => handleDateChange('startDate') },
                    error: (isShowErrors && !formState.data.startDate) || Boolean(formState.errors['startDate']),
                  }}
                  value={formState.data.startDate ?? undefined}
                  error={
                    isShowErrors && !formState.data.startDate ? REQUIRED_FIELD_MESSAGE : formState.errors.startDate
                  }
                  handleDateChange={handleDateChange('startDate')}
                  handleDateError={handleDateError}
                  minDate={minDate}
                  maxDate={maxDate}
                />

                <MonthYearDatePicker
                  name="endDate"
                  label="Ending (final date)"
                  required
                  slotProps={{
                    inputProps: { clearable: true, onClear: () => handleDateChange('endDate') },
                    error: (isShowErrors && !formState.data.endDate) || Boolean(formState.errors['endDate']),
                  }}
                  value={formState.data.endDate ?? undefined}
                  error={isShowErrors && !formState.data.endDate ? REQUIRED_FIELD_MESSAGE : formState.errors.endDate}
                  handleDateChange={handleDateChange('endDate')}
                  handleDateError={handleDateError}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </CoreBox>
            </>
          ) : null}

          <ContinueButton onContinue={onSaveAndContinue} isDisabled={!formState.isFormValid} />
        </StyledCard>
      )}
    </SectionAccordion>
  );
};
